import { BrowserRouter as Router, Routes, Route, useNavigate, Outlet, useLocation } from "react-router-dom";
import ProtectedRoute from "./features/auth/ProtectedRoute";
import { I18nextProvider } from "react-i18next";
import i18n from "./translation";
import { fetchUserInfoAction, selectUserInfo } from "./features/userInfo/userInfoSlice";
import { useEffect } from "react";
import TokenService from "./features/auth/tokenService";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import InfoMessageModal from "./components/InfoMessageModal";
import { useInfoMessage } from "./hooks/useInfoMessage";

import Search from "./features/search/Search";
import Search2 from "./features/search2/Search2";
import Bulk from "./features/address/Bulk";
import D4Weather2 from "./features/data4weather/D4Weather2";
import RiskEditor from "./features/riskEditor/RiskEditor";
import Settings from "./features/settings/Settings";
import Auth from "./features/auth/Auth";

import { Trans } from "react-i18next";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { CssBaseline } from "@mui/material";
import AppBar from "./features/address/AppBar";

import "./App.css";

// Configuration du message d'information
const INFO_MESSAGE_CONFIG = {
  version: 1, // Incrémenter ce nombre pour afficher un nouveau message
  title: "Nouvelle interface Data4Home",
  content: (
    <>
      <p>Nous avons le plaisir de vous proposer une nouvelle interface qui vous donne un accès plus rapide aux données.</p>
      <p style={{ marginTop: '12px' }}>
        Explorez les améliorations et n'hésitez pas à nous faire part de vos retours.
      </p>
    </>
  )
};

// no home yet, 
// check if the user is logged in
// if not, redirect to the login page
// if yes, redirect to the correct page based on the user's permissions 
const Home = () => {
  const userInfo = useAppSelector(selectUserInfo);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Gestion du message d'information avec système de versioning
  const { showMessage, closeMessage } = useInfoMessage(INFO_MESSAGE_CONFIG);

  // check if the user is logged in
  useEffect(function() {
    const accessToken = TokenService.getLocalAccessToken();
    if (!accessToken) {
      navigate("/auth/signin");
    }
  }, []);

  // redirect to the correct page based on the user's permissions
  useEffect(function() {
    if (!userInfo.value || location.pathname !== "/") {
      return;
    }

    if (userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home" && item.scope.includes("view")).length > 0) {
      navigate("/address");
    } else if (userInfo.value.rtp.filter((item: any) => item.resource === "vision_weather" && item.scope.includes("view")).length > 0) {
      navigate("/weather");
    }
  }, [userInfo]);
  
  /* TODO: loading page*/
  return (<>
    {!userInfo.value && <div>Loading...</div>}
    {/* in case of misconfiguration, the user might not have any access rights, show a dedicated page to identify the issue quickly */}
    {userInfo.value && userInfo.value.rtp.length === 0 && <NoAccessRightContent />}
    {userInfo.value && userInfo.value.rtp.length > 0 && <Outlet />}
    
    {/* Modal d'information avec système de versioning */}
    <InfoMessageModal
      open={showMessage}
      onClose={closeMessage}
      title={INFO_MESSAGE_CONFIG.title}
      content={INFO_MESSAGE_CONFIG.content}
    />
  </>)
}

export default function App() {
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector(selectUserInfo);

  // user is logged in, fetch the user info
  useEffect(function() {
    const accessToken = TokenService.getLocalAccessToken();
    if (accessToken) {
      dispatch(fetchUserInfoAction(accessToken));
    }
  }, [dispatch]);

  // initialize the analytics container
  useEffect(() => {
    if (process.env.NODE_ENV === "development") { (window as any).ANALYTICS_CONTAINER = process.env.REACT_APP_ANALYTICS_CONTAINER; }
    if ((window as any).ANALYTICS_CONTAINER === "__ANALYTICS_CONTAINER__") { return; }
    let _mtm = (window as any)._mtm = (window as any)._mtm || [];
    _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
    let d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
    g.async=true; g.src=`${(window as any).ANALYTICS_CONTAINER}`; s.parentNode?.insertBefore(g,s);
  }, [])

  // set the user id in the analytics container
  useEffect(function() {
    if ((window as any)._mtm && userInfo.value) {
      let _mtm = (window as any)._mtm = (window as any)._mtm || [];
      _mtm.push({'uid':`${userInfo.value.email}`});
    }
  }, [userInfo]) 

  return (
    <I18nextProvider i18n={i18n}>
      <Router>
        <Routes>
          <Route path="/" element={<Home />} >
            <Route
              path="address"
              element={
                <ProtectedRoute>
                  <Search2 />
                </ProtectedRoute>
              }
            />
            <Route
              path="address/legacy"
              element={
                <ProtectedRoute>
                  <Search />
                </ProtectedRoute>
              }
            />
            <Route
              path="address/bulk"
              element={
                <ProtectedRoute>
                  <Bulk />
                </ProtectedRoute>
              }
            />
            <Route
              path="weather"
              element={
                <ProtectedRoute>
                  <D4Weather2 />
                </ProtectedRoute>
              }
            />
            <Route
              path="risk-editor"
              element={
                <ProtectedRoute>
                  <RiskEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="exclusion-zone"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="/auth/*" element={<Auth />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Router>
    </I18nextProvider>
  );
}


const NotFoundPage = () => {
  return (
    <Box sx={{ display: "flex" }}>
      <div>test</div>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme: any) => theme.zIndex.drawer + 1 }}
      />
      <Box
      sx={{
        paddingLeft: 3,
        paddingRight: 3,
        paddingTop: 10,
        textAlign: "center",
        flex: 1,
      }}
      >
        <Trans i18nKey="notFoundLabel">This resource does not exist, go back to <Link href="/">Home</Link></Trans>
      </Box>
    </Box>
  )
}

const NoAccessRightContent = () => {
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme: any) => theme.zIndex.drawer + 1 }}
      />
      <Box
      sx={{
        paddingLeft: 3,
        paddingRight: 3,
        paddingTop: 10,
        textAlign: "center",
        flex: 1,
      }}
      >
        <Trans i18nKey="noAccessRightLabel">Something went wrong, please contact the administrator. (ERROR: access rights)</Trans>
      </Box>
    </Box>
  )
}
